import { CommonEvents } from "@common/services/common-events";
import FeaturesStore from "@common/stores/features-store";
import { makePurchaseStore } from "@common/stores/purchase-store";
import RealtimeSessionStore from "@common/stores/realtime-session-store";
import SpaceStore from "@common/stores/space-store";
import TimerStore from "@common/stores/timer-store";
import UserDetailsStore from "@common/stores/user-details-store";
import UserPreferencesStore from "@common/stores/user-preferences-store";
import UserStore from "@common/stores/user-store";
import CalendarStore from "@common/stores/calendar-store";
import { getTimerTunnelSyncState } from "@common/use-cases/timer/timer-tunnel-sync";
import { millisecondsToTimeStr } from "@common/utils/datetime";
import TodoGroupUtils from "@common/utils/todo-group-utils";
import TodoUtils from "@common/utils/todo-utils";
import { AreaSelectStore } from "@web/src/app/components/area-selection/area-select-store";
import { analytics } from "@web/src/app/use-cases/analytics";
import type { AllDndTypeBundle } from "@web/src/app/use-cases/drag-and-drop";
import { makeDropHandlers } from "@web/src/app/use-cases/drag-and-drop";
import { sendNotification } from "@web/src/app/use-cases/notification";
import { WebEvents } from "@web/src/app/web-events";
import {
  featureFlagService,
  Http,
  purchasesApi,
  realtimeService,
  spaceService,
  todoService,
  eventService,
  trpc,
  userService,
} from "@web/src/app/web-services";
import { DndStore } from "@web/src/hooks/dnd/dnd-store";
import HighlightStore from "@web/src/store/highlight-store";
import UIStore from "@web/src/store/ui-store";
import NotesStore from "@web/src/store/web-notes-store";
import WebTodoStore from "@web/src/store/web-todo-store";

// local storage but with promise return types
const asyncLocalStorage =
  typeof window === "undefined"
    ? {
        getItem: async (key: string) => null,
        setItem: async (key: string, value: string) => {},
        removeItem: async (key: string) => {},
      }
    : {
        async getItem(key: string) {
          return localStorage.getItem(key);
        },
        async setItem(key: string, value: string) {
          return localStorage.setItem(key, value);
        },
        async removeItem(key: string) {
          return localStorage.removeItem(key);
        },
      };

const timezone = {
  getTimezone: () => localStorage.getItem("timezone") ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export const highlightStore = new HighlightStore();
export const userStore = new UserStore({
  commonEvents: CommonEvents,
  localStorage: asyncLocalStorage,
  timezone,
  userService,
  purchasesApi,
  onAccessTokenChange: (token) => {
    Http.setAccessToken(token);
    trpc.setAccessToken(token);
  },
  getAccessToken: () => Http.getAccessToken(),
});
export const uiStore = new UIStore({ highlightStore, userStore, commonEvents: CommonEvents });
export const userPreferencesStore = new UserPreferencesStore({
  userService,
  localStorage: asyncLocalStorage,
});

const userDetailsStore = new UserDetailsStore({ trpcClient: trpc.client });

export const spaceStore = new SpaceStore({
  spaceService,
  userStore,
  makeTodoStore: (spaceId: string, spaceStore: SpaceStore) =>
    new WebTodoStore(spaceId, {
      userStore,
      todoService,
      spaceStore,
      commonEvents: CommonEvents,
      webEvents: WebEvents,
      highlightStore,
      analytics,
      localStorage: asyncLocalStorage,
    }),
  userPreferencesStore,
  commonEvents: CommonEvents,
});

const realtimeSessionStore = new RealtimeSessionStore({ realtimeService });

export const timerStore = new TimerStore({
  todos: {
    getTodoById: (todoId, spaceId) => {
      const todoStore = spaceStore.todoStoreMap[spaceId];
      if (!todoStore) throw new Error(`Space does not exist ${spaceId}`);
      return todoStore.todoIdMap[todoId] ?? null;
    },
    getNextTodo: (todoId, spaceId, groupKey) => {
      const todoStore = spaceStore.todoStoreMap[spaceId];
      if (!todoStore) throw new Error(`Space does not exist ${spaceId}`);

      const todo = todoStore.fullTodoIdMap[todoId];
      if (!todo) throw new Error(`Todo does not exist ${todoId}`);
      if (!groupKey)
        groupKey =
          TodoGroupUtils.groupKeysForTodo(
            todo,
            todoStore.displayMode,
            todoStore.fullTodoIdMap,
            todoStore.parentChildMap,
          )[0] ?? null;
      if (!groupKey) {
        console.warn(
          "Current todo not visible, could not find next todo because we don't know which group it belongs to",
        );
        return null;
      }

      return TodoUtils.nextTodo(
        todoStore.fullTodoIdMap,
        todoId,
        groupKey,
        todoStore.displayMode,
        todoStore.curHashtagFilterFn,
      );
    },
    updateTodo: (todoId, spaceId, value) => {
      const todoStore = spaceStore.todoStoreMap[spaceId];
      if (!todoStore) throw new Error(`Space does not exist ${spaceId}`);
      return todoStore.updateTodo(todoId, value);
    },
    setTodoComplete: (todoId, spaceId, complete, moveToTodayOnComplete) => {
      const todoStore = spaceStore.todoStoreMap[spaceId];
      if (!todoStore) throw new Error(`Space does not exist ${spaceId}`);
      return todoStore.markComplete(todoId, complete, moveToTodayOnComplete);
    },
  },
  notify: {
    todoComplete: (todo) => sendNotification(`Time's up!`, `${todo.value}`, "tick"),
    breakStart: (breakDuration) =>
      sendNotification("Break Time!", `Take a break for ${millisecondsToTimeStr(breakDuration)}`, "break"),
    breakOver: (todo) => sendNotification(`Break's Over!`, todo ? `Back to ${todo.value}` : "Choose next task", "tick"),
    timerStart: () => {},
  },
  localStorage: asyncLocalStorage,
  getDisplayMode: () => spaceStore.currentTodoStore.displayMode,
});

export const calendarStore = new CalendarStore({
  eventService,
  userPreferencesStore,
  userStore,
  spaceProvider: spaceStore,
});
export const notesStore = new NotesStore({ spaceStore, todoService, userStore });

const purchaseStore = makePurchaseStore({ purchasesApi });
const featuresStore = new FeaturesStore({ featureFlagService });

const areaSelectStore = new AreaSelectStore();

const dropHandlers = makeDropHandlers({ spaceStore, uiStore, areaSelectStore });
const dndStore = new DndStore<AllDndTypeBundle>(dropHandlers);

const stores = {
  highlightStore,
  notesStore,
  purchaseStore,
  spaceStore,
  timerStore,
  uiStore,
  userDetailsStore,
  userPreferencesStore,
  userStore,
  featuresStore,
  areaSelectStore,
  dndStore,
  realtimeSessionStore,
  calendarStore,
};

if (typeof window !== "undefined") {
  window.stores = stores;
}

export type Stores = typeof stores;

export default stores;
